import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { getDashboardRoute } from '../../utils/roleHelpers';
import { toPersian } from '../../utils/persianNumber';
import '../../styles/landing.css';
import AppBackground from '../../components/decoration/AppBackground';

// ─── scroll-reveal helper ─────────────────────────────────────────────
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Reveal({ children, delay = 0, style: extra }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={`lp-reveal ${inView ? 'lp-in' : ''}`} style={{ transitionDelay: `${delay}ms`, ...extra }}>
      {children}
    </div>
  );
}

// ─── small inline icon set (no external icon deps) ────────────────────
const ICONS = {
  brain: 'M9.5 2a3.5 3.5 0 00-3.5 3.5v.28A3.5 3.5 0 003 9v1a3.5 3.5 0 001.36 2.77A3.5 3.5 0 006 19a3.5 3.5 0 003.5-3.5v-10A3.5 3.5 0 009.5 2zM14.5 2a3.5 3.5 0 013.5 3.5v.28A3.5 3.5 0 0121 9v1a3.5 3.5 0 01-1.36 2.77A3.5 3.5 0 0118 19a3.5 3.5 0 01-3.5-3.5v-10A3.5 3.5 0 0114.5 2z',
  sparkles: 'M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8L12 2zM19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15z',
  network: 'M12 2a2 2 0 100 4 2 2 0 000-4zM4 18a2 2 0 100 4 2 2 0 000-4zM20 18a2 2 0 100 4 2 2 0 000-4zM12 6v6m0 0l-6 5m6-5l6 5',
  history: 'M3 12a9 9 0 109-9 9.75 9.75 0 00-7 3L3 8 M3 3v5h5 M12 7v5l4 2',
  target: 'M12 2a10 10 0 100 20 10 10 0 000-20zM12 6a6 6 0 100 12 6 6 0 000-12zM12 10a2 2 0 100 4 2 2 0 000-4z',
  bolt: 'M13 2L4 14h6l-1 8 9-12h-6l1-8z',
  book: 'M4 5a2 2 0 012-2h11a1 1 0 011 1v15a1 1 0 01-1 1H6a2 2 0 01-2-2V5z M4 19a2 2 0 002 2h11 M8 7h7 M8 11h7',
  refresh: 'M4 4v5h5 M20 20v-5h-5 M4.5 15a8 8 0 0014.9 3.1M19.5 9a8 8 0 00-14.9-3.1',
  chart: 'M4 20V10 M10 20V4 M16 20v-7 M22 20H2',
  shield: 'M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z',
  check: 'M20 6L9 17l-5-5',
  ticket: 'M2 9a3 3 0 000 6v2a2 2 0 002 2h16a2 2 0 002-2v-2a3 3 0 000-6V7a2 2 0 00-2-2H4a2 2 0 00-2 2v2z',
  clock: 'M12 22a10 10 0 100-20 10 10 0 000 20z M12 6v6l4 2',
  users: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2 M9 7a4 4 0 100 8 4 4 0 000-8z M23 21v-2a4 4 0 00-3-3.87 M16 3.13a4 4 0 010 7.75',
  archive: 'M21 8v13H3V8 M1 3h22v5H1z M10 12h4',
};

function Icon({ name, size = 20, color = 'currentColor', strokeWidth = 2 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[name] || ICONS.sparkles} />
    </svg>
  );
}

// ─── data ───────────────────────────────────────────────────────────
const FEATURES = [
  { icon: 'brain', title: 'تحلیل هوشمند تیکت', desc: 'هوش مصنوعی محتوای هر درخواست را می‌خواند و به‌درستی دسته‌بندی و اولویت‌بندی می‌کند.' },
  { icon: 'sparkles', title: 'پیشنهادهای هوشمند', desc: 'راهکارهای پیشنهادی بر اساس تجربه‌های مشابه گذشته، در همان لحظه در اختیار کارشناس قرار می‌گیرد.' },
  { icon: 'network', title: 'عیب‌یابی هوشمند شبکه', desc: 'مشکلات زیرساخت و شبکه سریع‌تر شناسایی می‌شوند و راه‌حل مناسب پیشنهاد می‌شود.' },
  { icon: 'history', title: 'تاریخچه کامل تیکت‌ها', desc: 'تمام درخواست‌ها و پاسخ‌های قبلی، همیشه در دسترس و قابل جست‌وجو باقی می‌مانند.' },
  { icon: 'target', title: 'پیگیری لحظه‌ای', desc: 'وضعیت هر درخواست را از لحظه ثبت تا رفع نهایی مشکل، شفاف دنبال کنید.' },
  { icon: 'bolt', title: 'پاسخ‌گویی سریع‌تر', desc: 'با اولویت‌بندی خودکار، درخواست‌های مهم سریع‌تر به دست کارشناس مربوطه می‌رسد.' },
  { icon: 'book', title: 'پایگاه دانش', desc: 'راهکارهای پرتکرار در یک مرکز دانش سازمان‌یافته گردآوری و قابل استفاده مجدد است.' },
  { icon: 'refresh', title: 'یادگیری مستمر', desc: 'سامانه با گذشت زمان و ثبت هر تیکت جدید، دقیق‌تر و شخصی‌سازی‌شده‌تر عمل می‌کند.' },
  { icon: 'chart', title: 'داشبورد تحلیلی', desc: 'عملکرد تیم پشتیبانی را با گزارش‌ها و نمودارهای دقیق زیر نظر بگیرید.' },
  { icon: 'shield', title: 'ذخیره‌سازی امن', desc: 'تمام اطلاعات و تیکت‌های شما مطابق بالاترین استانداردهای امنیتی نگهداری می‌شود.' },
];

const BENEFITS = [
  'هیچ درخواست پشتیبانی گم نمی‌شود',
  'کاهش محسوس هزینه‌های پشتیبانی',
  'کاهش قابل توجه زمان پاسخ‌گویی',
  'افزایش رضایت و وفاداری مشتریان',
  'عیب‌یابی هوشمند با کمک هوش مصنوعی',
  'بهبود مستمر عملکرد از طریق یادگیری ماشین',
  'مدیریت متمرکز و یکپارچه همه تیکت‌ها',
];

const STATS = [
  { value: '+۵۰۰۰', label: 'تیکت پردازش‌شده' },
  { value: '٪۹۸', label: 'رضایت مشتریان' },
  { value: '٪۶۰', label: 'کاهش زمان رفع مشکل' },
  { value: '۲۴/۷', label: 'پشتیبانی بی‌وقفه' },
];

const STEPS = [
  { icon: 'ticket', title: 'ثبت تیکت', desc: 'کاربر درخواست یا مشکل خود را ثبت می‌کند.' },
  { icon: 'brain', title: 'تحلیل هوشمند', desc: 'هوش مصنوعی متن تیکت را تحلیل و دسته‌بندی می‌کند.' },
  { icon: 'sparkles', title: 'پیشنهاد راه‌حل', desc: 'راهکارهای مرتبط از سوابق گذشته پیشنهاد می‌شود.' },
  { icon: 'users', title: 'بررسی کارشناس', desc: 'کارشناس مربوطه درخواست را بررسی می‌کند.' },
  { icon: 'check', title: 'رفع مشکل', desc: 'پاسخ نهایی ارسال و مشکل برطرف می‌شود.' },
  { icon: 'archive', title: 'آرشیو هوشمند', desc: 'تیکت برای یادگیری بهتر سامانه ذخیره می‌شود.' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // Already logged in — go straight to the dashboard instead of the pitch.
  useEffect(() => {
    if (!loading && user) navigate(getDashboardRoute(user.type), { replace: true });
  }, [user, loading, navigate]);

  const year = new Date().getFullYear();

  return (
    <div dir="rtl" style={{ background: 'var(--surface-1)', color: 'var(--gray-800)', overflowX: 'hidden' }}>
      {/* ── Navbar ─────────────────────────────────────────────── */}
      <header style={s.nav}>
        <div style={s.navInner}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/pejvak-mark.svg" alt="پژواک رایانه" width={34} height={34} style={{ borderRadius: 8 }} />
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--gray-900)' }}>پژواک رایانه</span>
          </div>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
            <a href="#features" className="lp-link-hover" style={s.navLink}>امکانات</a>
            <a href="#how-it-works" className="lp-link-hover" style={s.navLink}>روند کار</a>
            <a href="#benefits" className="lp-link-hover" style={s.navLink}>مزایا</a>
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => navigate('/sign_up')} className="lp-btn-hover" style={s.btnGhost}>ایجاد حساب</button>
            <button onClick={() => navigate('/login')} className="lp-btn-hover" style={s.btnPrimary}>ورود به سامانه</button>
          </div>
        </div>
      </header>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section style={{ ...s.section, paddingTop: 64, position: 'relative' }}>
        <AppBackground style={{ position: 'absolute' }} />
        <div style={{ ...s.container, ...s.heroGridWrap }} className="lp-hero-grid">
          <div className="fade-in">
            <div style={s.badge}>
              <Icon name="sparkles" size={14} color="var(--purple-600)" />
              پشتیبانی مبتنی بر هوش مصنوعی
            </div>
            <h1 style={s.h1}>
              هوشمندتر از همیشه،<br />
              <span className="lp-gradient-text">پشتیبانی مشتریان</span> را متحول کنید
            </h1>
            <p style={s.heroDesc}>
              سامانه تیکتینگ پژواک رایانه با تحلیل هوشمند درخواست‌ها، اولویت‌بندی خودکار و پیشنهادهای مبتنی بر
              هوش مصنوعی، پاسخ‌گویی به مشتریان را سریع‌تر، دقیق‌تر و کاملاً قابل پیگیری می‌کند؛ بدون هیچ گزارش
              گم‌شده‌ای و با تاریخچه‌ای کامل از هر درخواست.
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 32, flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/login')} className="lp-btn-hover" style={{ ...s.btnPrimary, padding: '14px 28px', fontSize: 15 }}>
                ورود به سامانه
              </button>
              <button onClick={() => navigate('/sign_up')} className="lp-btn-hover" style={{ ...s.btnSecondary, padding: '14px 28px', fontSize: 15 }}>
                ایجاد حساب رایگان
              </button>
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 36, flexWrap: 'wrap' }}>
              {['بدون نیاز به نصب', 'پشتیبانی فارسی', 'امنیت سازمانی'].map((t) => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--gray-600)' }}>
                  <Icon name="check" size={14} color="var(--green-600)" /> {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────────────────── */}
      <section style={{ ...s.section, paddingTop: 40 }}>
        <div style={s.container}>
          <Reveal>
            <div style={s.statsBar} className="lp-grid">
              {STATS.map((st) => (
                <div key={st.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 34, fontWeight: 800, color: 'var(--gray-900)' }} className="lp-gradient-text">{st.value}</div>
                  <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 6 }}>{st.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────── */}
      <section id="features" style={s.section}>
        <div style={s.container}>
          <Reveal>
            <SectionHeading eyebrow="امکانات" title="همه‌چیز برای یک تیم پشتیبانی هوشمند" subtitle="از ثبت تا آرشیو، هر مرحله از پشتیبانی با کمک هوش مصنوعی ساده‌تر می‌شود." />
          </Reveal>
          <div className="lp-grid" style={{ marginTop: 40 }}>
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 4) * 60}>
                <div className="lp-card-hover" style={s.featureCard}>
                  <div style={s.featureIcon}><Icon name={f.icon} size={22} color="var(--blue-600)" /></div>
                  <h3 style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--gray-900)', margin: '14px 0 6px' }}>{f.title}</h3>
                  <p style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.8, margin: 0 }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ───────────────────────────────────────── */}
      <section id="how-it-works" style={{ ...s.section, background: 'var(--surface-2)' }}>
        <div style={s.container}>
          <Reveal>
            <SectionHeading eyebrow="روند کار" title="از ثبت درخواست تا رفع نهایی مشکل" subtitle="یک مسیر شفاف و هوشمند که هر تیکت را دنبال می‌کند." />
          </Reveal>
          <div className="lp-steps-grid" style={{ marginTop: 40 }}>
            {STEPS.map((st, i) => (
              <Reveal key={st.title} delay={i * 70}>
                <div style={s.stepCard}>
                  <div style={s.stepNum}>{toPersian(i + 1).padStart(2, '۰')}</div>
                  <div style={s.stepIcon}><Icon name={st.icon} size={20} color="var(--blue-600)" /></div>
                  <h4 style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--gray-900)', margin: '10px 0 4px' }}>{st.title}</h4>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', lineHeight: 1.7, margin: 0 }}>{st.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ───────────────────────────────────────────── */}
      <section id="benefits" style={s.section}>
        <div style={{ ...s.container, ...s.heroGridWrap }} className="lp-hero-grid">
          <Reveal>
            <div style={{ position: 'relative' }}>
              <div style={{ ...s.benefitsGlow }} />
              <div style={s.benefitsPanel}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
                  <div style={{ ...s.floatIcon, background: 'var(--blue-50)', color: 'var(--blue-600)' }}><Icon name="shield" size={18} /></div>
                  <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--gray-900)' }}>چرا پژواک؟</span>
                </div>
                {BENEFITS.map((b) => (
                  <div key={b} style={s.benefitRow}>
                    <div style={s.checkDot}><Icon name="check" size={13} color="#fff" strokeWidth={3} /></div>
                    <span style={{ fontSize: 13.5, color: 'var(--gray-700)' }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div>
              <SectionHeading eyebrow="مزایا" title="سرمایه‌گذاری روی رضایت مشتریان شما" subtitle="پژواک به تیم پشتیبانی شما کمک می‌کند سریع‌تر، هوشمندتر و با اطمینان بیشتری کار کند." align="right" />
              <p style={{ fontSize: 13.5, color: 'var(--gray-600)', lineHeight: 2, marginTop: 16 }}>
                با گذر زمان، سامانه از تیکت‌ها و پاسخ‌های پیشین می‌آموزد و پیشنهادهایی دقیق‌تر و شخصی‌سازی‌شده‌تر ارائه می‌دهد؛
                نتیجه این یادگیری مستمر، کارشناسانی است که سریع‌تر به نتیجه می‌رسند و مشتریانی که تجربه‌ای به‌مراتب بهتر دارند.
              </p>
              <button onClick={() => navigate('/login')} className="lp-btn-hover" style={{ ...s.btnPrimary, marginTop: 24, padding: '13px 26px' }}>
                همین حالا شروع کنید
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Final CTA ──────────────────────────────────────────── */}
      <section style={{ ...s.section }}>
        <div style={s.container}>
          <Reveal>
            <div style={s.ctaBanner}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: '#fff', margin: 0 }}>مدیریت هوشمند پشتیبانی را همین امروز آغاز کنید</h2>
                <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.85)', marginTop: 10 }}>
                  به تیم‌هایی بپیوندید که با پژواک، پشتیبانی خود را سریع‌تر و هوشمندتر کرده‌اند.
                </p>
                <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button onClick={() => navigate('/login')} className="lp-btn-hover" style={{ ...s.btnPrimary, background: '#fff', color: 'var(--blue-700)', padding: '13px 28px' }}>
                    ورود به سامانه
                  </button>
                  <button onClick={() => navigate('/sign_up')} className="lp-btn-hover" style={{ ...s.btnGhost, borderColor: 'rgba(255,255,255,0.4)', color: '#fff', padding: '13px 28px' }}>
                    ایجاد حساب
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer style={{ borderTop: '1px solid var(--gray-200)', padding: '48px 0 28px', background: 'var(--surface-2)' }}>
        <div style={s.container}>
          <div className="lp-footer-grid">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <img src="/pejvak-mark.svg" alt="پژواک رایانه" width={28} height={28} style={{ borderRadius: 7 }} />
                <span style={{ fontWeight: 800, fontSize: 14.5, color: 'var(--gray-900)' }}>پژواک رایانه نقش جهان</span>
              </div>
              <p style={{ fontSize: 12.5, color: 'var(--gray-500)', lineHeight: 1.9, maxWidth: 280 }}>
                سامانه هوشمند مدیریت ارتباط با مشتری و پشتیبانی فنی، طراحی‌شده برای تیم‌های پشتیبانی و شبکه.
              </p>
            </div>
            <FooterCol title="محصول" links={[['امکانات', '#features'], ['روند کار', '#how-it-works'], ['مزایا', '#benefits']]} />
            <FooterCol title="حساب کاربری" links={[['ورود به سامانه', '/login'], ['ایجاد حساب', '/sign_up']]} onNav={navigate} />
            <FooterCol title="ارتباط با ما" links={[['crm.pejvakrayane.com', 'https://crm.pejvakrayane.com'], ['پشتیبانی: info@pejvakrayane.com', 'mailto:info@pejvakrayane.com']]} />
          </div>
          <div style={{ borderTop: '1px solid var(--gray-200)', marginTop: 36, paddingTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>
            © {toPersian(year)} پژواک رایانه نقش جهان. تمامی حقوق محفوظ است.
          </div>
        </div>
      </footer>
    </div>
  );
}

function FooterCol({ title, links, onNav }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 14 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {links.map(([label, href]) => (
          <a key={label} href={href.startsWith('/') ? undefined : href}
            onClick={href.startsWith('/') ? (e) => { e.preventDefault(); onNav?.(href); } : undefined}
            className="lp-link-hover" style={{ fontSize: 12.5, color: 'var(--gray-500)' }}>
            {label}
          </a>
        ))}
      </div>
    </div>
  );
}

function SectionHeading({ eyebrow, title, subtitle, align = 'center' }) {
  return (
    <div style={{ textAlign: align === 'center' ? 'center' : 'right', maxWidth: align === 'center' ? 620 : 'none', margin: align === 'center' ? '0 auto' : 0 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--blue-600)', letterSpacing: 0.4, marginBottom: 10 }}>{eyebrow}</div>
      <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--gray-900)', margin: 0 }}>{title}</h2>
      <p style={{ fontSize: 13.5, color: 'var(--gray-500)', marginTop: 12, lineHeight: 1.8 }}>{subtitle}</p>
    </div>
  );
}

const s = {
  nav: {
    position: 'sticky', top: 0, zIndex: 50,
    background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)',
    borderBottom: '1px solid var(--gray-200)',
  },
  navInner: {
    maxWidth: 1180, margin: '0 auto', padding: '14px 24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  navLink: { fontSize: 13.5, fontWeight: 600, color: 'var(--gray-600)' },
  container: { maxWidth: 1180, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 },
  section: { padding: '80px 0' },
  heroGridWrap: { paddingTop: 0 },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '6px 14px', borderRadius: 20,
    background: 'var(--purple-50)', color: 'var(--purple-600)',
    fontSize: 12, fontWeight: 700, marginBottom: 20,
    border: '1px solid var(--purple-100)',
  },
  h1: { fontSize: 42, fontWeight: 800, color: 'var(--gray-900)', lineHeight: 1.35, margin: 0 },
  heroDesc: { fontSize: 14.5, color: 'var(--gray-600)', lineHeight: 2, marginTop: 20, maxWidth: 540 },
  btnPrimary: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '10px 20px', borderRadius: 10, fontSize: 13.5, fontWeight: 700,
    background: 'linear-gradient(135deg,var(--blue-500),var(--blue-700))',
    color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 8px 20px rgba(35,58,122,0.35)',
  },
  btnSecondary: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '10px 20px', borderRadius: 10, fontSize: 13.5, fontWeight: 700,
    background: '#fff', color: 'var(--blue-700)', border: '1.5px solid var(--blue-200)',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnGhost: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '9px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
    background: 'transparent', color: 'var(--gray-700)', border: '1.5px solid var(--gray-200)',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  floatCard: {
    position: 'absolute', display: 'flex', alignItems: 'center', gap: 10,
    background: '#fff', borderRadius: 14, padding: '10px 14px',
    boxShadow: '0 12px 30px rgba(15,23,42,0.12)', border: '1px solid var(--gray-100)',
    minWidth: 190,
  },
  floatIcon: {
    width: 34, height: 34, borderRadius: 9,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  statsBar: {
    background: 'linear-gradient(135deg, var(--blue-50), var(--purple-50))',
    border: '1px solid rgba(35,58,122,0.12)', borderRadius: 24,
    padding: '36px 24px',
  },
  featureCard: {
    background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 16,
    padding: 22, height: '100%',
  },
  featureIcon: {
    width: 44, height: 44, borderRadius: 12, background: 'var(--blue-50)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  stepCard: {
    background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 16,
    padding: 20, position: 'relative', height: '100%',
  },
  stepNum: {
    position: 'absolute', top: 14, left: 16, fontSize: 22, fontWeight: 800,
    color: 'var(--gray-200)',
  },
  stepIcon: {
    width: 38, height: 38, borderRadius: 10, background: 'var(--blue-50)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  benefitsGlow: {
    position: 'absolute', inset: -20, borderRadius: 32,
    background: 'linear-gradient(135deg, rgba(35,58,122,0.10), rgba(139,92,246,0.10))',
    filter: 'blur(30px)', zIndex: 0,
  },
  benefitsPanel: {
    position: 'relative', zIndex: 1, background: '#fff',
    border: '1px solid var(--gray-200)', borderRadius: 20, padding: 28,
  },
  benefitRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--gray-100)' },
  checkDot: {
    width: 22, height: 22, borderRadius: '50%', background: 'var(--green-600)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  ctaBanner: {
    position: 'relative', overflow: 'hidden', textAlign: 'center',
    borderRadius: 28, padding: '56px 32px',
    background: 'linear-gradient(135deg, var(--blue-600), var(--purple-600))',
  },
};
